import { createContext, useCallback, useContext } from 'react';

import { SystemError } from 'platform/Error';
import { usePageStalenessTimer } from 'platform/hooks/useReloadStalePage';
import { useObservability } from 'platform/observability';

import { useAuthContext } from './AuthContext';

export interface ReauthOptions {
  /** Called if reauth was successful. */
  onSuccess: () => void;
  /** Called if reauth was canceled. */
  onCancel: () => void;
  /**
   * Title of reauth UI.
   * Default: "Sign in again to continue"
   */
  title?: string;
}

export interface ReauthResult {
  showReauth: (options: ReauthOptions) => void;
}

/** Context for global reauth functionality. */
export const ReauthContext = createContext<ReauthResult>({
  showReauth: () => {
    throw new SystemError('missing ReauthContext provider');
  },
});

/** Hook to access global reauth functionality. */
export const useReauth = () => {
  return useContext(ReauthContext);
};

/**
 * @returns Function to track + refresh auth state
 * @throws if refresh fails
 */
export const useRefreshAuthState = () => {
  const { trackEvent } = useObservability();
  const { authValidatedTime } = usePageStalenessTimer();
  const { refresh } = useAuthContext();

  return useCallback(async () => {
    trackEvent(
      'stale-auth-revalidate',
      'first-navigation-after-window',
      { timeSinceAuthValidatedMs: Date.now() - authValidatedTime },
      // Adds a custom datadog time event in the user's session
      { addTiming: true },
    );
    await refresh();
  }, [authValidatedTime, refresh, trackEvent]);
};
